import { Injectable } from "@angular/core";
import { WebsocketRequest, WebsocketService } from "@app/shared/databases/websocket";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { catchError, from, map, Observable, of, switchMap, tap } from "rxjs";
import { DealerBranding, DealerBrandingDBFile, DealerBrandingListItem } from "../model/dealer-branding";
import { DealerBrandingActionPayload } from "../store/actions/dealer-branding.actions";

export interface S3SaveRequestData {
    /**
     *Key used to locate and identify uploaded image
     *
     * @type {string}
     * @memberof S3SaveRequestData
     */
    key: string;
    /**
     *Base64 binary string
     *
     * @type {string}
     * @memberof S3SaveRequestData
     */
    data64: string;
}
export interface S3LoadRequestData {
    /**
     *Key used to locate and identify uploaded image
     *
     * @type {string}
     * @memberof S3SaveRequestData
     */
    key: string;
}
export interface S3ZipRequestData {
    /**
     *Key used to locate and identify uploaded image
     *
     * @type {string}
     * @memberof S3SaveRequestData
     */
    [key: string]: string;
}
export interface S3SaveRequest extends WebsocketRequest {
    /**
     *Key and binary string data necessary for successful save request
     *
     * @type {S3SaveRequestData}
     * @memberof S3SaveRequest
     */
    s3_save_data: S3SaveRequestData;
}

export interface S3LoadRequest extends WebsocketRequest {
    /**
     *Key data necessary for successful load request
     *
     * @type {S3LoadRequestData}
     * @memberof S3LoadRequest
     */
    s3_load_data: S3LoadRequestData;
}
export interface S3ZipRequest extends WebsocketRequest {
    /**
     *Key data necessary for successful load request
     *
     * @type {S3ZipRequestData}
     * @memberof S3ZipRequest
     */
    s3_zip_data: S3ZipRequestData;
}

export interface S3ApiRequestData extends WebsocketRequest {
    s3_data: { method: string; params: { entity_id: number; filename: string; data64: string } };
}

enum REQUESTTYPE {
    SAVE = "s3_save",
    LOAD = "s3_load",
    ZIP = "s3_zip",
}

@Injectable({
    providedIn: "root",
})
export class DealerBrandingDbService {
    constructor(private readonly websocket: WebsocketService) {}

    /**
     *Converts image file to base64 binary string
     *
     * @param {Blob} imageFile Image file to be converted
     * @returns {Observable<string>} Returns the converted binary string as a Promise value
     */
    convertImage(imageFile: Blob): Observable<string> {
        return from(
            new Promise<string>(resolve => {
                const reader = new FileReader();
                if (imageFile) {
                    reader.onload = img => {
                        const re = /D[A-Z0-9:/;]*base64,/i;
                        const imgToBinaryArray = img.target.result.toString().replace(re, "");
                        resolve(imgToBinaryArray);
                    };
                    reader.readAsDataURL(imageFile);
                }
            })
        );
    }

    /**
     *Converts Dealer Branding image to base64 binary string, then upload to server
     *
     * @param {DealerBranding} image image file to be converted to binary string
     * @returns {Observable<{dealerBranding:DealerBranding}>} Store is updated with new Dealer Branding Image upon successful save
     * @memberof DealerBrandingDbService
     */
    saveImageData(image: DealerBranding) {
        return this.convertImage(image.imageFile).pipe(
            switchMap((bin: string) => {
                const data: S3SaveRequestData = {
                    key: image.key,
                    data64: bin,
                };
                return this.websocket.makeRequest({
                    request_type: REQUESTTYPE.SAVE,
                    s3_save_data: data,
                    metadata: {},
                } as S3SaveRequest);
            }),
            map(() => ({ dealerBranding: image })),
            catchError(DataServiceError.handleError())
        );
    }

    /**
     *Uses key data to request image file, then converts it from binary string
     *
     * @param {S3LoadRequestData} data Key data used to request image file
     * @returns {Observable<{dealerBranding:DealerBranding}>} Updates store with loaded Dealer Branding image
     * @memberof DealerBrandingDbService
     */
    loadImageData(data: S3LoadRequestData) {
        return this.websocket
            .makeRequest({
                request_type: REQUESTTYPE.LOAD,
                s3_load_data: data,
                metadata: {},
            } as S3LoadRequest)
            .pipe(
                map((response: string) => {
                    const array = this.decodeImage(response);
                    const imageFile = new Blob([array]);
                    const dealerBranding: DealerBranding = { key: data.key, imageFile };
                    return { dealerBranding };
                }),
                catchError(err => {
                    if (err.includes("NoSuchKey")) {
                        // set imageFile to null if image not found so subscribers know it returned
                        const dealerBranding = { key: data.key, imageFile: null };
                        return of({ dealerBranding });
                    }
                    throw new Error(err);
                })
            );
    }
    /**
     *Decodes base64 binary string and returns a unicode array
     *
     * @private
     * @param {string} base64Image Base64 binary string
     * @returns {Uint8Array} Unicode array used to create image file
     * @memberof DealerBrandingDbService
     */
    decodeImage(base64Image: string): Uint8Array {
        const decodedString = atob(base64Image);
        const bytes = new Uint8Array(decodedString.length);
        return bytes.map((byte, i) => decodedString.charCodeAt(i));
    }

    getZipFile(data: S3ZipRequestData) {
        return this.websocket
            .makeRequest({
                request_type: REQUESTTYPE.ZIP,
                s3_zip_data: data,
                metadata: {},
            } as S3ZipRequest)
            .pipe(
                map((response: string) => {
                    const array = this.decodeImage(response);
                    return new Blob([array], { type: "application/zip" });
                }),
                catchError(DataServiceError.handleError())
            );
    }

    getEncodedZipFile(data: S3ZipRequestData) {
        return this.websocket
            .makeRequest({
                request_type: REQUESTTYPE.ZIP,
                s3_zip_data: data,
                metadata: {},
            } as S3ZipRequest)
            .pipe(catchError(DataServiceError.handleError()));
    }

    public loadDealerBrandingImageList$(entityId: number): Observable<DealerBrandingListItem[]> {
        return this.websocket
            .makeRequest<string>({
                request_type: "s3",
                s3_data: {
                    method: "LockoutList",
                    params: {
                        entity_id: entityId,
                    },
                },
            } as S3ApiRequestData)
            .pipe(
                map((res: string) => this.parse<DealerBrandingListItem[]>(res)),
                catchError(DataServiceError.handleError())
            );
    }

    public deleteDealerBrandingImage$(payload: DealerBrandingActionPayload): Observable<string> {
        return this.websocket
            .makeRequest<string>({
                request_type: "s3",
                s3_data: {
                    method: "LockoutDelete",
                    params: {
                        entity_id: payload.entityId,
                        filename: payload.fileName,
                    },
                },
            } as S3ApiRequestData)
            .pipe(catchError(DataServiceError.handleError()));
    }

    public createDealerBrandingImage$(payload: DealerBrandingActionPayload): Observable<string> {
        return this.convertImage(payload.body).pipe(
            switchMap((bin: string) =>
                this.websocket.makeRequest<string>({
                    request_type: "s3",
                    s3_data: {
                        method: "LockoutPut",
                        params: {
                            entity_id: payload.entityId,
                            filename: payload.fileName,
                            data64: bin,
                        },
                    },
                } as any)
            ),
            tap(_ => payload.callbackEmitter.emit()),
            catchError(DataServiceError.handleError())
        );
    }

    public loadDealerBrandingImageByFileName$(payload: DealerBrandingActionPayload): Observable<DealerBranding> {
        return this.websocket
            .makeRequest<string>({
                request_type: "s3",
                s3_data: {
                    method: "LockoutGet",
                    params: {
                        entity_id: payload.entityId,
                        filename: payload.fileName,
                    },
                },
            } as S3ApiRequestData)
            .pipe(
                map((res: string) => {
                    const dbFile = this.parse<DealerBrandingDBFile>(res);
                    const array = this.decodeImage(dbFile.data64);
                    const imageFile = new Blob([array]);
                    const dealerBranding: DealerBranding = { key: dbFile.key, imageFile };
                    return dealerBranding;
                }),
                catchError(DataServiceError.handleError())
            );
    }

    private parse<T = any>(data: string): T {
        return JSON.parse(data) as T;
    }
}
